import { Box, Divider, Text } from '@chakra-ui/react'
import { GlobalLayout, Loader } from '@components/layout/PageLayout'
import { productDisplays } from '@components/product/component-options'
import RelatedProductFeed from '@components/product/feeds/related-product-feed'
import ProductDetailsTab from '@components/product/product-details/product-tab'
import ProductNotFound from '@components/product/product-not-found'
import Seo, { reformStoreFromHeaders } from '@components/seo/seo'
import ActiveLink from '@components/ui/active-link'
import { BreadcrumbItems } from '@components/ui/breadcrumb'
import Container from '@components/ui/container'
import { ProductPageContext } from '@contexts/product-page'
import { useStore } from '@contexts/store'
import { serverProductByIDorSlug } from '@framework/middleware-helpers'
import { Product, Variant } from '@framework/model-types/Product'
import { useGetShopProductBySlugOrID } from '@framework/products/get-shop-products'
import { generateInitialAttributes } from '@helper/generate-initial-attributes'
import useBreadcrumb, { convertBreadcrumbTitle } from '@hooks/use-breadcrumb'
import { useRecentlyViewedProducts } from '@hooks/use-recently-viewed-products'
import { ROUTES } from '@utils/routes'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { IoChevronForward, IoHomeOutline } from 'react-icons/io5'
import { SWRConfig } from 'swr'

type ProductProps = {
  slug: string
  productObj: Product
}

function ProductPage({ slug, productObj }: ProductProps) {
  const { storeData: shop, storeTemplate, analytics } = useStore()
  const router = useRouter()
  const pageTopRef = useRef<HTMLDivElement>(null)

  const options = {
    keepPreviousData: true,
    fallbackData: { data: productObj },
  }

  const {
    product: productData,
    isLoading,
    notFound,
    mutate: mutateAPIProductData,
  } = useGetShopProductBySlugOrID(shop?._id, slug, options)

  const product = useMemo(() => {
    if (!productData && productObj) {
      return productObj
    }
    return productData
  }, [productObj, productData])

  const breadcrumbs = useBreadcrumb()
  const [selectedVariation, setSelectedVariation] = useState<Variant | null>(
    null,
  )

  const { attributes: productAttributes, variants } = product

  const variations = useMemo(() => {
    return (
      variants?.map((variation) => {
        return {
          ...variation,
          keys: variation.options.map((option) => option.id).sort(),
        }
      }) ?? []
    )
  }, [variants])

  const { query } = router
  const { variant: urlVariant } = query

  const [attributes, setAttributes] = useState<Record<string, string>>(() =>
    generateInitialAttributes(
      variations,
      urlVariant as string,
      productAttributes,
      setSelectedVariation,
      storeTemplate,
    ),
  )

  useEffect(() => {
    analytics.track('Product page viewed', { ...product })
  }, [product, analytics])

  const [recentlyViewProducts, addToRecentlyViewedProducts] =
    useRecentlyViewedProducts()

  const pageContextValue = useMemo(() => {
    const correspondingVariant = product?.variants?.find(
      (variant: any) => variant?._id === selectedVariation?._id,
    )

    return {
      product,
      selectedVariation,
      setSelectedVariation,
      mutateAPIProductData,
      attributes,
      setAttributes,
      variations,
      showProductSpecs:
        (correspondingVariant &&
          (Boolean(correspondingVariant?.weight?.value) ||
            Object(correspondingVariant?.dimension)?.values?.some(Boolean))) ||
        (!correspondingVariant &&
          (Boolean(product?.weight?.value) ||
            Object(product?.dimension)?.values?.some(Boolean))),
    }
  }, [
    product,
    selectedVariation,
    setSelectedVariation,
    mutateAPIProductData,
    attributes,
    variations,
  ])

  useEffect(
    () => {
      if (!product) return
      const newRecentlyViewProducts = recentlyViewProducts?.filter(
        (p: typeof product) => p?._id !== product?._id,
      )

      newRecentlyViewProducts?.unshift(product)
      if (newRecentlyViewProducts.length > 5) newRecentlyViewProducts.pop()
      addToRecentlyViewedProducts(newRecentlyViewProducts)
    },
    //eslint-disable-next-line
    [isLoading, notFound, product],
  )

  if (!product) return <Loader />
  if (notFound) return <ProductNotFound />

  const ThemeProductDisplay = productDisplays[storeTemplate]

  return (
    <>
      <Divider />
      <div className="pt-6 lg:pt-7">
        <Container>
          <Box mb="3">
            <BreadcrumbItems
              separator={
                <IoChevronForward className="text-skin-base text-opacity-40 text-15px" />
              }
            >
              <ActiveLink
                href={ROUTES.HOME}
                activeClassName="font-semibold text-heading"
              >
                <a className="inline-flex items-center">
                  <IoHomeOutline className="me-1.5 text-skin-base text-15px" />
                  <Text pl="1">Home</Text>
                </a>
              </ActiveLink>

              {breadcrumbs?.map(
                (breadcrumb: any, index: number, arr: any[]) => {
                  const name =
                    index === arr.length - 1
                      ? product?.name
                      : breadcrumb?.breadcrumb
                  return (
                    <ActiveLink
                      href={breadcrumb.href}
                      activeClassName="font-semibold text-heading"
                      key={breadcrumb.href}
                    >
                      <a className="capitalize">
                        {convertBreadcrumbTitle(name)}
                      </a>
                    </ActiveLink>
                  )
                },
              )}
            </BreadcrumbItems>
          </Box>
          <ProductPageContext.Provider value={pageContextValue}>
            <ThemeProductDisplay pageTopRef={pageTopRef} />
            <ProductDetailsTab />
          </ProductPageContext.Provider>
        </Container>
      </div>

      <RelatedProductFeed
        product={product}
        uniqueKey="related-products"
        pageTopRef={pageTopRef}
      />
    </>
  )
}

const ProductPageWrapper = ({
  slug,
  fallback,
  shop,
}: {
  slug: string
  fallback: any
  shop: any
}) => {
  const product: any = Object.values(fallback)[0]
  return (
    <SWRConfig value={{ fallback }}>
      <Seo
        title={`${shop?.name} - ${product?.name ?? ''}`}
        description={`${product?.name} - ${product?.description ?? ''}`}
        path={`${shop?.siteUrl}/products/${product?.slug ?? ''}`}
        site_url={shop?.siteUrl}
        image={product?.media ?? ''}
      />
      <ProductPage
        slug={slug}
        productObj={Object.values(fallback)[0] as Product}
      />
    </SWRConfig>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const storeDetails = reformStoreFromHeaders(context)
  const { slug } = context.query
  const { data, success, key }: { data: any; success: boolean; key: string } =
    await serverProductByIDorSlug(storeDetails?.id, slug)
  const fallback: any = {}
  if (success) {
    fallback[key] = data
  }
  return {
    props: {
      slug,
      fallback,
      shop: storeDetails,
    },
  }
}

ProductPageWrapper.Layout = GlobalLayout

export default ProductPageWrapper
